import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Mail, Building2, Phone, Briefcase, Linkedin, Globe, Save, Inbox, Sparkles, Clock, Users2, User as UserIcon, Pencil, X, Check } from 'lucide-react';
import AdminHeader from './AdminHeader';
import ContactMeetings from './ContactMeetings';
import { useAdminAuth } from '../context/AdminAuthContext';
import { crmContact, crmUpdateContact, contactColleagues } from '../lib/marketingApi';

const STAGES = ['new', 'subscriber', 'lead', 'engaged', 'customer', 'cold'];

function fmt(iso, withTime) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', withTime ? { dateStyle: 'medium', timeStyle: 'short' } : { dateStyle: 'medium' }); } catch (e) { return iso; }
}

function ContactEdit({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="mb-0.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-[#0035A8]" />
    </label>
  );
}

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'editor';
  const [c, setC] = useState(null);
  const [stage, setStage] = useState('');
  const [note, setNote] = useState('');
  const [owner, setOwner] = useState('');
  const [savingOwner, setSavingOwner] = useState(false);
  const [saving, setSaving] = useState(false);
  const [colleagues, setColleagues] = useState({ company: null, items: [] });

  const load = () => crmContact(id).then((d) => { setC(d.contact); setStage(d.contact.stage || 'new'); setOwner(d.contact.owner || ''); }).catch(() => toast.error('Failed to load contact'));
  useEffect(() => { load(); }, [id]);
  useEffect(() => { contactColleagues(id).then(setColleagues).catch(() => {}); }, [id]);

  const saveStage = async (s) => {
    setStage(s); setSaving(true);
    try { const d = await crmUpdateContact(id, { stage: s }); setC(d.contact); toast.success('Stage updated'); }
    catch (e) { toast.error('Update failed'); } finally { setSaving(false); }
  };
  const saveOwner = async () => {
    setSavingOwner(true);
    try { const d = await crmUpdateContact(id, { owner }); setC(d.contact); toast.success('Owner saved'); }
    catch (e) { toast.error('Save failed'); } finally { setSavingOwner(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try { const d = await crmUpdateContact(id, { note }); setC(d.contact); setNote(''); toast.success('Note added'); }
    catch (e) { toast.error('Failed'); } finally { setSaving(false); }
  };

  const startEdit = () => {
    setEdit({ name: c.name || '', company: c.company || '', job_title: c.job_title || '', phone: c.phone || '' });
    setEditing(true);
  };
  const saveEdit = async () => {
    setSavingEdit(true);
    try { const d = await crmUpdateContact(id, edit); setC(d.contact); setEditing(false); toast.success('Contact updated'); }
    catch (e) { toast.error('Update failed'); } finally { setSavingEdit(false); }
  };

  if (!c) return (<div className="min-h-screen bg-slate-50"><AdminHeader active="contacts" /><div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div></div>);

  const sub = c.subscription || {};
  const socials = c.socials || {};
  const Field = ({ icon: I, label, value }) => value ? (
    <div className="flex items-center gap-2 text-sm text-slate-700"><I className="h-4 w-4 shrink-0 text-slate-400" /><span className="truncate">{value}</span></div>
  ) : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-detail">
      <AdminHeader active="contacts" />
      <div className="mx-auto max-w-6xl px-5 py-6 sm:px-8">
        <button onClick={() => navigate('/admin/contacts')} className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" /> Contacts</button>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Left: identity + subscription */}
          <div className="space-y-4 lg:col-span-1">
            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0035A8]/10 text-lg font-bold text-[#0035A8]">{(c.name || c.email).charAt(0).toUpperCase()}</span>
                  <div className="min-w-0">
                    <div className="truncate text-lg font-bold text-slate-900">{c.name || '—'}</div>
                    <div className="truncate text-sm text-slate-500">{c.email}</div>
                  </div>
                </div>
                {canEdit && !editing && (
                  <button onClick={startEdit} data-testid="contact-edit-btn" className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#0035A8] hover:text-[#0035A8]"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                )}
              </div>
              {editing ? (
                <div className="mt-4 space-y-2.5" data-testid="contact-edit-form">
                  <ContactEdit label="Name" value={edit.name} onChange={(v) => setEdit({ ...edit, name: v })} testid="contact-edit-name" />
                  <ContactEdit label="Company" value={edit.company} onChange={(v) => setEdit({ ...edit, company: v })} testid="contact-edit-company" />
                  <ContactEdit label="Job title" value={edit.job_title} onChange={(v) => setEdit({ ...edit, job_title: v })} testid="contact-edit-job-title" />
                  <ContactEdit label="Phone" value={edit.phone} onChange={(v) => setEdit({ ...edit, phone: v })} testid="contact-edit-phone" />
                  <div className="flex justify-end gap-2 pt-1">
                    <button onClick={() => setEditing(false)} data-testid="contact-edit-cancel" className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50"><X className="h-3.5 w-3.5" /> Cancel</button>
                    <button onClick={saveEdit} disabled={savingEdit} data-testid="contact-edit-save" className="inline-flex h-8 items-center gap-1 rounded-md bg-[#0035A8] px-3.5 text-xs font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">{savingEdit ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save</button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 space-y-2">
                  <Field icon={Building2} label="Company" value={c.company} />
                  <Field icon={Briefcase} label="Title" value={c.job_title} />
                  <Field icon={Phone} label="Phone" value={c.phone} />
                  <Field icon={Mail} label="Email" value={c.email} />
                  {socials.linkedin ? <a href={socials.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#0035A8] hover:underline"><Linkedin className="h-4 w-4" /> LinkedIn</a> : null}
                  {socials.website ? <a href={socials.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#0035A8] hover:underline"><Globe className="h-4 w-4" /> Website</a> : null}
                </div>
              )}
              <div className="mt-4 border-t border-slate-100 pt-3">
                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Lifecycle stage</div>
                <select value={stage} onChange={(e) => saveStage(e.target.value)} disabled={!canEdit || saving} data-testid="contact-stage"
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm capitalize outline-none focus:border-[#0035A8]">
                  {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="mt-3 flex flex-wrap gap-1">
                  {(c.sources || []).map((s) => <span key={s} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">{s}</span>)}
                </div>
              </div>
              <div className="mt-4 border-t border-slate-100 pt-3">
                <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400"><UserIcon className="h-3.5 w-3.5" /> Assigned to</div>
                <div className="flex gap-2">
                  <input value={owner} onChange={(e) => setOwner(e.target.value)} disabled={!canEdit || savingOwner} data-testid="contact-owner" placeholder="Assign owner…"
                    className="h-9 flex-1 rounded-md border border-slate-300 bg-white px-3 text-sm outline-none focus:border-[#0035A8] disabled:bg-slate-50" />
                  {canEdit && <button onClick={saveOwner} disabled={savingOwner} data-testid="contact-owner-save" className="rounded-md bg-slate-900 px-3.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-60">{savingOwner ? '…' : 'Save'}</button>}
                </div>
                {c.last_contacted_at ? <div className="mt-2 text-xs text-slate-400">Last contacted {fmt(c.last_contacted_at, true)}</div> : null}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="contact-subscription">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Mail className="h-4 w-4 text-[#0035A8]" /> Newsletter</div>
              {sub.status === 'subscribed' ? (
                <>
                  <span className="mt-2 inline-flex rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700">Subscribed</span>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(sub.categories || []).map((cat) => <span key={cat} className="rounded-full bg-[#0035A8]/10 px-2 py-0.5 text-xs font-medium text-[#0035A8]">{cat.replace(/_/g, ' ')}</span>)}
                  </div>
                </>
              ) : <p className="mt-2 text-sm text-slate-400">{sub.status === 'unsubscribed' ? 'Unsubscribed' : 'Not subscribed'}</p>}
            </div>

            {colleagues.company && colleagues.items.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="contact-colleagues">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Users2 className="h-4 w-4 text-[#0035A8]" /> Colleagues at {colleagues.company}</div>
                <div className="mt-3 space-y-2">
                  {colleagues.items.map((p) => (
                    <button key={p.id} onClick={() => navigate(`/admin/contacts/${p.id}`)} data-testid={`colleague-${p.email}`} className="flex w-full items-center justify-between rounded-md border border-slate-100 px-3 py-2 text-left hover:border-[#0035A8]/40 hover:bg-slate-50">
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-slate-800">{p.name || p.email}</span>
                        <span className="block truncate text-xs text-slate-400">{p.job_title || p.email}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: enquiries + AI enrichment + activity */}
          <div className="space-y-4 lg:col-span-2">
            {c.enrichment ? (
              <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-5" data-testid="contact-enrichment">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-800"><Sparkles className="h-4 w-4" /> AI enrichment</div>
                <div className="mt-2 space-y-1 text-sm text-slate-700">
                  {c.enrichment.company_summary ? <p>{c.enrichment.company_summary}</p> : null}
                  {c.enrichment.talking_points ? <ul className="mt-1 list-disc pl-5">{(Array.isArray(c.enrichment.talking_points) ? c.enrichment.talking_points : [c.enrichment.talking_points]).map((t, i) => <li key={i}>{t}</li>)}</ul> : null}
                </div>
              </div>
            ) : null}

            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Inbox className="h-4 w-4 text-[#0035A8]" /> Enquiries ({(c.enquiries || []).length})</div>
              <div className="mt-3 space-y-3">
                {(c.enquiries || []).length === 0 ? <p className="text-sm text-slate-400">No enquiries from this contact.</p> :
                  c.enquiries.map((e) => (
                    <button key={e.id} onClick={() => navigate(`/admin/enquiries/${e.id}`)} data-testid={`contact-enquiry-${e.id}`}
                      className="block w-full rounded-md border border-slate-200 p-3 text-left hover:border-[#0035A8]/40 hover:bg-slate-50">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{e.segment || 'enquiry'} · {e.status || 'new'}</span>
                        <span className="text-xs text-slate-400">{fmt(e.created_at)}</span>
                      </div>
                      <p className="mt-1 line-clamp-2 text-sm text-slate-700">{e.message || e.interest || '—'}</p>
                    </button>
                  ))}
              </div>
            </div>

            <ContactMeetings contactId={id} canEdit={canEdit} onLogged={load} />

            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Clock className="h-4 w-4 text-[#0035A8]" /> Activity</div>
              {canEdit && (
                <div className="mt-3 flex gap-2">
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" data-testid="contact-note-input"
                    className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#0035A8]" />
                  <button onClick={addNote} disabled={saving} data-testid="contact-note-save" className="inline-flex items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60"><Save className="h-4 w-4" /> Add</button>
                </div>
              )}
              <div className="mt-3 space-y-2.5">
                {[...(c.activities || [])].reverse().map((a) => (
                  <div key={a.id} className="flex gap-3 text-sm">
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#0035A8]/40" />
                    <div>
                      <div className="text-slate-700"><span className="font-medium capitalize">{a.type}</span> — {a.detail}</div>
                      <div className="text-xs text-slate-400">{fmt(a.at, true)}{a.source ? ` · ${a.source}` : ''}</div>
                    </div>
                  </div>
                ))}
                {(c.activities || []).length === 0 ? <p className="text-sm text-slate-400">No activity yet.</p> : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
